import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { SEMPLY_APP_LABELS, type SocialSignupPreview } from '@authsemply/shared';
import { AuthCard } from '../components/AuthCard';
import { Alert } from '../components/Alert';
import { api, ApiError } from '../lib/api';
import { messageOuverture, useOuverture } from '../lib/ouverture';
import { TermsLabel } from '../components/TermsLabel';

/**
 * Écran de confirmation après un retour de fournisseur externe (Google,
 * Microsoft, Apple) sur une adresse inconnue — servi en
 * `/inscription-google`, `/inscription-microsoft`, `/inscription-apple`.
 *
 * Rien n'a encore été créé : le profil vérifié attend, scellé dans un cookie
 * de 15 minutes. Le compte n'existe qu'après le clic « Créer mon compte »,
 * qui porte aussi l'acceptation des CGU — même principe que le formulaire
 * classique, et que le parcours Google de SEmplyApp. Le fournisseur est lu
 * dans le profil scellé, pas dans l'URL.
 */
export function SocialSignupPage() {
  const navigate = useNavigate();
  const [preview, setPreview] = useState<SocialSignupPreview | null>(null);
  const [expired, setExpired] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accepted, setAccepted] = useState(false);
  const [pending, setPending] = useState(false);
  // Ouverture de la suite (27/09/2026) : sur invitation, le code se saisit ici,
  // au moment où le compte est vraiment créé.
  const ouverture = useOuverture();
  const [codeInvitation, setCodeInvitation] = useState('');

  useEffect(() => {
    api
      .get<SocialSignupPreview>('/oauth/signup')
      .then(setPreview)
      .catch(() => setExpired(true));
  }, []);

  async function confirm() {
    setPending(true);
    setError(null);
    try {
      const { location } = await api.post<{ location: string }>('/oauth/signup', {
        acceptTerms: true,
        signupCode: codeInvitation.trim() || undefined,
      });
      window.location.assign(location);
    } catch (err) {
      // Profil déjà utilisé ou expiré : même écran que la demande expirée.
      if (err instanceof ApiError && err.code === 'oauth_signup_expired') {
        setExpired(true);
        return;
      }
      const refus =
        err instanceof ApiError
          ? (messageOuverture(err.code, err.message) ?? (err.code === 'oauth_autre_compte_rattache' ? err.message : null))
          : null;
      setError(refus ?? "La création du compte a échoué. Réessayez depuis la page de connexion.");
      setPending(false);
    }
  }

  async function cancel() {
    await api.delete('/oauth/signup').catch(() => undefined);
    navigate('/login');
  }

  if (expired) {
    return (
      <AuthCard title="Demande expirée">
        <Alert message="Cette demande d'inscription a expiré ou a déjà été traitée." />
        <Link className="btn btn-secondary" to="/login">
          Retour à la connexion
        </Link>
      </AuthCard>
    );
  }

  if (!preview) {
    return <AuthCard title="Chargement…">{null}</AuthCard>;
  }

  const fullName = [preview.firstName, preview.lastName].filter(Boolean).join(' ');

  return (
    <AuthCard
      title="Créer votre compte SEmply ?"
      subtitle={`Aucun compte n'existe pour cette adresse ${preview.providerLabel}. Voici ce qui sera créé :`}
    >
      <Alert message={error} />

      <div className="signup-preview">
        {preview.avatarUrl ? (
          <img src={preview.avatarUrl} alt="" width={48} height={48} referrerPolicy="no-referrer" />
        ) : null}
        <div>
          {fullName ? <strong>{fullName}</strong> : null}
          <div>{preview.email}</div>
        </div>
      </div>

      {/* « Masquer mon adresse » d'Apple : SEmply n'écrira qu'au relais, qui
          fait suivre. Mieux vaut le dire avant la création qu'après. */}
      {preview.relayEmail ? (
        <p className="subtitle" style={{ marginBottom: '0.5rem' }}>
          Vous avez choisi de masquer votre adresse : nos e-mails vous parviendront par le
          relais d’Apple.
        </p>
      ) : null}

      <p className="subtitle" style={{ marginBottom: '0.5rem' }}>
        Un seul compte pour {Object.values(SEMPLY_APP_LABELS).join(', ')}.
      </p>

      {ouverture?.signupMode === 'CLOSED' ? (
        <Alert message={ouverture.signupClosedMessage || 'Les inscriptions sont fermées pour le moment.'} />
      ) : null}

      {ouverture?.invitationRequired ? (
        <div className="field">
          <label htmlFor="signupCode">Code d'invitation</label>
          <input
            id="signupCode"
            name="signupCode"
            autoComplete="off"
            required
            value={codeInvitation}
            onChange={(event) => setCodeInvitation(event.target.value)}
          />
        </div>
      ) : null}

      <label style={{ fontSize: '0.85rem', display: 'block', margin: '0 0 1rem' }}>
        <input
          type="checkbox"
          checked={accepted}
          onChange={(event) => setAccepted(event.target.checked)}
        />{' '}
        <TermsLabel />
      </label>

      <button
        className="btn"
        type="button"
        disabled={
          !accepted ||
          pending ||
          ouverture?.signupMode === 'CLOSED' ||
          Boolean(ouverture?.invitationRequired && !codeInvitation.trim())
        }
        onClick={() => void confirm()}
      >
        {pending ? 'Création…' : 'Créer mon compte'}
      </button>
      <button
        className="btn btn-secondary"
        style={{ marginTop: '0.5rem' }}
        type="button"
        disabled={pending}
        onClick={() => void cancel()}
      >
        Annuler
      </button>
    </AuthCard>
  );
}
